import { restartSubscription } from "@/controllers/subscription";
import { effectiveCheckoutParams } from "@/lib/checkoutIntent";
import { useState, useEffect } from "react";
import { useRouter } from "next/router";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { codeAppliedState, paymentModalOpenState, planState, promoCodeDisabledState, showAppSubscriberModalState, subscriptionState, userDataState } from "@/lib/store";

const useApplyPromoCode = (setSnackBar?: (snack: { open: boolean; text: string; severity: "success" | "error" | "info" | "warning" }) => void) => {
    const [promoCode, setPromoCode] = useState("");
    const setSubscription = useSetAtom(subscriptionState);
    const [plan, setPlan] = useAtom(planState);
    const userData = useAtomValue(userDataState);
    const setShowSubscriberDrawer = useSetAtom(paymentModalOpenState);
    const setShowAppSubscriberModal = useSetAtom(showAppSubscriberModalState);
    const setCodeApplied = useSetAtom(codeAppliedState);
    const setPromoCodeDisabled = useSetAtom(promoCodeDisabledState);

    const { replace, query } = useRouter();

    // URL query wins; the persisted checkout intent fills the gap so the
    // interval from a shared link survives query-stripping hops (signup,
    // /upgrade redirect). See src/lib/checkoutIntent.ts.
    const effT = effectiveCheckoutParams(query).t;
    const interval = effT === "monthly" ? "monthly" : effT === "annual" || effT === "annually" ? "annually" : (query.pt as string) === "m" ? "monthly" : "annually";

    useEffect(() => {
        const promoCodeSession = query.promo as string;
        setPromoCode(promoCodeSession);
    }, [query]);

    const applyPromoCode = (codeParam?) => {
        restartSubscription(codeParam ? codeParam : promoCode).then((res) => {
            // Anonymous "visitor" users (and some error paths) get no plans back —
            // restartSubscription normalises those to null. Leave state untouched
            // instead of crashing on res.plans.find below.
            if (!res || !Array.isArray(res.plans)) return;
            setSubscription(res);

            const newPlan = plan ? res.plans.find((p) => p.plan_id === plan.plan_id) : res.plans.find((plan) => plan.product_name.includes("Advanced") && plan.plan_interval === interval);
            const discountInfo: any = res.payment_data?.plans?.find((p) => p.id === newPlan?.plan_id);
            setPlan(newPlan);

            if (res.payment_data?.invalid_promo_code) {
                !userData.app_subscriber_only &&
                    setSnackBar &&
                    setSnackBar({
                        open: true,
                        text: "This discount code is not valid",
                        severity: "error",
                    });
                setPromoCode("");
            } else {
                !userData.app_subscriber_only &&
                    setSnackBar &&
                    discountInfo &&
                    newPlan &&
                    setSnackBar({
                        open: true,
                        text: `${discountInfo.discount_pc ? discountInfo.discount_pc + "%" : ""} Discount applied: New price ${newPlan.currency_symbol} ${discountInfo.price}`,
                        severity: "success",
                    });
                if (discountInfo?.discount_pc === 100) {
                    setSnackBar &&
                        setSnackBar({
                            open: true,
                            text: `Nothing to pay: Welcome to ORTEX`,
                            severity: "success",
                        });
                    setShowAppSubscriberModal(false);
                    setShowSubscriberDrawer(false);
                    replace("/markets-overview");
                }
                setCodeApplied(codeParam ? codeParam : promoCode);
                setPromoCodeDisabled(true);
            }
        });
    };

    return { applyPromoCode };
};

export default useApplyPromoCode;
